"use client"

import React, { useState, useEffect } from "react"
import { useUser } from "@clerk/clerk-react"
import { usePathname } from "@/lib/router"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent } from "@/components/ui/card"
import { UserAvatar } from "@/components/user/user-avatar"
import { useAuthActions, useProfileStore } from "@/stores"
import { useOnboardingTranslations } from "@/hooks/use-translations"
import { 
  ArrowRight, Check, Brain, Calendar,
  Clock, User, CheckCircle, Layers,
} from "lucide-react"
import { Progress } from "@/components/ui/progress"
import { sanitize } from "@/utils/sanitize"
import { toast } from "sonner"
import Image from "@/components/ui/image"
import { AreasStep, type AreaDraft } from "@/components/onboarding/areas-step"
import { HoursStep } from "@/components/onboarding/hours-step"
import {
  draftToDayShape,
  saveDayShape,
  HOURS_DRAFT_DEFAULTS,
  type HoursDraft,
} from "@/lib/convex-day-shape"
import { useAreasStore } from "@/stores"

interface LocalProfileData {
  name?: string;
  avatar_url?: string;
  has_seen_onboarding?: boolean;
}

export function OnboardingTour() {
  const t = useOnboardingTranslations()
  
  const TOUR_STEPS = [
    {
      title: t("tour_welcome_title"),
      description: t("tour_welcome_description"),
      image: "https://static.blitztask.com/onboarding/onboarding1.png",
      icon: <Check className="h-8 w-8 text-green-500" />,
    },
    {
      title: t("tour_areas_title"),
      description: t("tour_areas_description"),
      interactive: "areas" as const,
      icon: <Layers className="h-8 w-8 text-indigo-500" />,
    },
    {
      title: t("tour_hours_title"),
      description: t("tour_hours_description"),
      interactive: "hours" as const,
      icon: <Clock className="h-8 w-8 text-sky-500" />,
    },
    {
      title: t("tour_brain_dump_title"),
      description: t("tour_brain_dump_description"),
      image: "https://static.blitztask.com/onboarding/onboarding2.png",
      icon: <Brain className="h-8 w-8 text-purple-500" />,
    },
    {
      title: t("tour_calendar_title"),
      description: t("tour_calendar_description"),
      image: "https://static.blitztask.com/onboarding/onboarding3.png",
      icon: <Calendar className="h-8 w-8 text-blue-500" />,
    },
    {
      title: t("tour_timer_title"),
      description: t("tour_timer_description"),
      image: "https://static.blitztask.com/onboarding/onboarding4.png",
      icon: <Clock className="h-8 w-8 text-amber-500" />,
    },
    {
      title: t("tour_complete_title"),
      description: t("tour_complete_description"),
      image: "https://static.blitztask.com/onboarding/onboarding5.png",
      icon: <CheckCircle className="h-8 w-8 text-emerald-500" />,
    },
  ]
  const [open, setOpen] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [hasSeenTour, setHasSeenTour] = useState(true) // Default to true to prevent flashing
  const [showProfileSetup, setShowProfileSetup] = useState(false)
  const [profileName, setProfileName] = useState("")
  const [profile, setProfile] = useState<LocalProfileData | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false)
  const [progress, setProgress] = useState(0)
  const [selectedAreas, setSelectedAreas] = useState<AreaDraft[]>([])
  const [areasSaved, setAreasSaved] = useState(false)
  const [hours, setHours] = useState<HoursDraft>({ ...HOURS_DRAFT_DEFAULTS })
  const [hoursSaved, setHoursSaved] = useState(false)
  const [showSkipWarning, setShowSkipWarning] = useState(false)
  // Whether each question has been *seen and left*, which is not the same as
  // having produced a value: picking no areas at all is a real answer, and
  // someone who gave it should not be warned about skipping what they already
  // decided. This is what the skip warning reads.
  const [areasAnswered, setAreasAnswered] = useState(false)
  const [hoursAnswered, setHoursAnswered] = useState(false)
  const createArea = useAreasStore((state) => state.createArea)
  
  const { user } = useAuthActions()
  // Clerk owns name and avatar. Read them from Clerk rather than the Convex
  // mirror: `ensureUser` only copies them across when a new token is minted,
  // so the mirror lags by up to a token lifetime — long enough for this dialog
  // to decide the name is still missing and reopen itself.
  const { user: clerkUser } = useUser()
  const { profile: cachedProfile, updateProfile: updateCachedProfile, ensureProfileLoaded } = useProfileStore()
  const pathname = usePathname()
  const clerkName = clerkUser?.fullName?.trim() || ""

  useEffect(() => {
    const checkProfileAndTourStatus = async () => {
      try {
        // Fix TypeScript error by adding null check for user
        if (!user) return
        
        // Use cached profile data if available, otherwise fetch from database
        let profileData: LocalProfileData | null = null
        
        if (cachedProfile) {
          profileData = {
            name: cachedProfile.name || undefined,
            avatar_url: cachedProfile.avatar_url || undefined,
            has_seen_onboarding: cachedProfile.has_seen_onboarding
          }
        } else {
          // Via the profile store (which reads /api/user/profile); querying
          // Supabase from the browser has no session to satisfy RLS under Clerk.
          const loaded = await ensureProfileLoaded()
          if (!loaded) {
            console.error("Error checking onboarding status: profile unavailable")
            return
          }

          profileData = {
            name: loaded.name || undefined,
            avatar_url: loaded.avatar_url || undefined,
            has_seen_onboarding: loaded.has_seen_onboarding
          }
        }

        const hasSeenOnboarding = profileData?.has_seen_onboarding ?? false
        // Clerk decides whether a name exists. Asking the Convex mirror here
        // would reopen this dialog on every run until the token refreshed.
        const nameMissing = !clerkName

        setProfile({
          name: clerkName || undefined,
          avatar_url: clerkUser?.imageUrl || undefined,
          has_seen_onboarding: profileData?.has_seen_onboarding
        })
        if (clerkName) {
          setProfileName(clerkName)
        }

        // Passwords are Clerk's concern now — there is no password step here.
        if (nameMissing) {
          setShowProfileSetup(true)
        } else {
          setHasSeenTour(hasSeenOnboarding)
          setOpen(!hasSeenOnboarding)
        }
      } catch (err) {
        console.error("Failed to check onboarding status:", err)
      }
    }
    
    if (user && pathname === "/") {
      checkProfileAndTourStatus()
    }
  }, [user, pathname, cachedProfile, clerkName, clerkUser?.imageUrl])


  // Simulate progress
  const simulateProgress = () => {
    let currentProgress = 0
    const interval = setInterval(() => {
      currentProgress += Math.random() * 15
      if (currentProgress > 95) {
        currentProgress = 100
        clearInterval(interval)
      }
      setProgress(Math.min(currentProgress, 100))
    }, 200)
    
    return () => clearInterval(interval)
  }

  /**
   * Writes the name to Clerk, not to Convex.
   *
   * Clerk owns it, and this dialog decides whether to show itself by asking
   * Clerk — so the write has to land there or the dialog reopens the moment
   * the profile store publishes. `ensureUser` mirrors it into Convex on the
   * next token, which nothing here waits on.
   *
   * Clerk has no `fullName` setter, so the typed value is split on the first
   * space: everything before it is the first name, the remainder the last.
   */
  const handleProfileSubmit = async () => {
    if (!profileName.trim() || !clerkUser) return
    setIsProcessing(true)
    const clearProgressSimulation = simulateProgress()

    try {
      const clean = sanitize(profileName.trim())
      const separator = clean.indexOf(" ")
      const firstName = separator === -1 ? clean : clean.slice(0, separator)
      const lastName = separator === -1 ? "" : clean.slice(separator + 1).trim()

      await clerkUser.update({ firstName, lastName })

      clearProgressSimulation()
      setIsProcessing(false)
      setShowProfileSetup(false)
      setHasSeenTour(false)
      setOpen(true)
    } catch (err) {
      console.error("Failed to update profile:", err)
      clearProgressSimulation()
      setIsProcessing(false)
      toast.error(t("failed_to_save_name"))
    }
  }

  /**
   * Profile picture, straight into Clerk. Restores what the onboarding step
   * lost when the Convex upload path was deleted — the image just lives with
   * Clerk now instead of in Convex file storage.
   */
  const handleAvatarFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ""
    if (!file || !clerkUser) return

    if (!file.type.startsWith("image/")) {
      toast.error(t("avatar_must_be_image"))
      return
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t("avatar_too_large"))
      return
    }

    try {
      setIsUploadingAvatar(true)
      await clerkUser.setProfileImage({ file })
      // `imageUrl` is read off the Clerk user, so pull the fresh one through.
      await clerkUser.reload()
    } catch (err) {
      console.error("Failed to set profile image:", err)
      toast.error(t("avatar_upload_failed"))
    } finally {
      setIsUploadingAvatar(false)
    }
  }

  /**
   * Writes the areas picked on the interactive step.
   *
   * Guarded by `areasSaved` because the step can be left more than once —
   * going back and forward again must not create a second "Family".
   */
  const persistAreas = async () => {
    if (areasSaved || selectedAreas.length === 0) return

    // Marked before the awaits: a double-click on Next would otherwise get past
    // the guard while the first batch is still in flight.
    setAreasSaved(true)

    try {
      // Sequential, so `position` comes out in the order they were picked.
      for (const area of selectedAreas) {
        await createArea({ name: area.name, color: area.color, icon: area.icon, scope: area.scope })
      }
    } catch (err) {
      console.error("Failed to create areas:", err)
      toast.error(t("areas_step_failed"))
      setAreasSaved(false)
    }
  }

  /**
   * Writes the working window and focus band.
   *
   * Written even when the defaults were left untouched: the point is that the
   * user has now *been asked*, and an explicit 9–18 they agreed to is worth
   * more to the planner than an implicit one they never saw. An invalid window
   * is skipped rather than blocking the tour — the step shows the error, and
   * Settings can still fix it.
   */
  const persistHours = async () => {
    if (hoursSaved) return
    if (hours.workdayEndMinutes <= hours.workdayStartMinutes) return

    setHoursSaved(true)

    try {
      await saveDayShape(draftToDayShape(hours))
    } catch (err) {
      console.error("Failed to save working hours:", err)
      toast.error(t("hours_step_failed"))
      setHoursSaved(false)
    }
  }

  const handleNext = async () => {
    if (currentTourStep.interactive === "areas") {
      await persistAreas()
      setAreasAnswered(true)
    }

    if (currentTourStep.interactive === "hours") {
      await persistHours()
      setHoursAnswered(true)
    }

    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(currentStep + 1)
    } else {
      handleComplete()
    }
  }

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleComplete = async () => {
    setOpen(false)

    if (!user) return

    try {
      await updateCachedProfile({ has_seen_onboarding: true })

      setHasSeenTour(true)
    } catch (err) {
      console.error("Failed to update onboarding status:", err)
    }
  }

  /**
   * Skipping is allowed, but not silently.
   *
   * Two of these steps are not a tour — they are the only questions the planner
   * needs answered, and the rest of the app stays quiet until they are. Someone
   * who dismisses a welcome dialog out of habit has no way of knowing that, so
   * the skip says what it costs and offers to go there instead. It is still a
   * skip: nobody is trapped in a dialog on their first visit.
   */
  const handleSkip = () => {
    if (!areasAnswered || !hoursAnswered) {
      setShowSkipWarning(true)
      return
    }
    void handleComplete()
  }

  /** Sends them to the first question they have not answered yet. */
  const handleSkipCancel = () => {
    setShowSkipWarning(false)
    const target = TOUR_STEPS.findIndex((step) =>
      step.interactive === "areas"
        ? !areasAnswered
        : step.interactive === "hours"
          ? !hoursAnswered
          : false,
    )
    if (target !== -1) setCurrentStep(target)
  }

  const currentTourStep = TOUR_STEPS[currentStep]
  const isLastStep = currentStep === TOUR_STEPS.length - 1

  if (hasSeenTour && !showProfileSetup) return null

  return (
    <>

      {/* Profile Setup Dialog */}
      {showProfileSetup && (
        <Dialog 
          open={showProfileSetup} 
          onOpenChange={(open) => {
            if (!open && !profileName.trim()) return // Prevent closing if name is empty
            setShowProfileSetup(open)
          }}
        >
          <DialogContent className="sm:max-w-[500px] [&>button:last-child]:hidden" onInteractOutside={(e) => e.preventDefault()}>
            <DialogHeader>
              <div className="flex items-center justify-center mb-4">
                <User className="h-10 w-10 text-primary" />
              </div>
              <DialogTitle className="text-2xl text-center font-bold">{t("welcome_to_blitztask")}</DialogTitle>
              <DialogDescription className="text-center mt-2">
                {t("lets_personalize_your_experience")}
              </DialogDescription>
            </DialogHeader>

            <div className="mt-6 space-y-6">
              {/* Using the UserAvatar component */}
              <div className="flex flex-col items-center justify-center space-y-4">
                <div className="relative h-24 w-24 flex items-center justify-center">
                  <UserAvatar
                    avatarUrl={clerkUser?.imageUrl}
                    name={profileName}
                    size="xl"
                  />
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarFile}
                    disabled={isUploadingAvatar}
                    className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                    aria-label={t("avatar_upload_description")}
                  />
                </div>
                <div className="text-sm text-center text-muted-foreground">
                  {isUploadingAvatar ? t("processing") : t("avatar_upload_description")}
                </div>
              </div>
              
              {/* Profile name section */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm text-muted-foreground">
                  {t("how_should_we_call_you")}
                </Label>
                <Input 
                  id="name"
                  placeholder={t("your_name")} 
                  value={profileName}
                  onChange={(e) => setProfileName(e.target.value)}
                  className="w-full"
                />
              </div>
              
              {isProcessing && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>{t("setting_up_your_profile")}</span>
                    <span>{Math.round(progress)}%</span>
                  </div>
                  <Progress value={progress} className="h-1" />
                </div>
              )}
            </div>

            <DialogFooter>
              <Button 
                onClick={handleProfileSubmit}
                disabled={!profileName.trim() || isProcessing}
                className="w-full"
              >
                {isProcessing ? t("processing") : t("continue_to_app")}
                {!isProcessing && <ArrowRight className="ml-2 h-4 w-4" />}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Onboarding Tour Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[550px] [&>button:last-child]:hidden" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
          <Button variant="outline" onClick={handleSkip} className="absolute top-4 right-4">
                  {t("skip")}
                </Button>
            <div className="flex items-center justify-center mb-4">
              {currentTourStep.icon}
            </div>
            <DialogTitle className="text-xl text-center">{currentTourStep.title}</DialogTitle>
            <DialogDescription className="text-center">{currentTourStep.description}</DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {currentTourStep.interactive === "areas" ? (
              <AreasStep selected={selectedAreas} onChange={setSelectedAreas} />
            ) : currentTourStep.interactive === "hours" ? (
              <HoursStep value={hours} onChange={setHours} />
            ) : (
              <Card className="overflow-hidden">
                <CardContent className="p-0">
                  <Image
                    src={currentTourStep.image || "/placeholder.svg"}
                    alt={currentTourStep.title}
                    height={224}
                    width={498}
                    className="w-full h-56 object-cover"
                  />
                </CardContent>
              </Card>
            )}
          </div>

          {/* Segments share the width rather than each claiming 4rem: the tour
              gained a step and fixed-width dots would have run past the dialog. */}
          <div className="mt-4 flex gap-1.5">
            {TOUR_STEPS.map((_, index) => (
              <div
                key={index}
                className={`h-2 flex-1 rounded-full transition-colors ${
                  index === currentStep ? "bg-primary" : "bg-muted"
                }`}
              />
            ))}
          </div>

          <DialogFooter className="flex justify-between mt-4">
            <div>
              {currentStep > 0 && (
                <Button variant="outline" onClick={handleBack}>
                  {t("back")}
                </Button>
              )}
            </div>
            
            <div className="flex gap-3">
              {!isLastStep ? (
                <Button onClick={() => void handleNext()}>
                  {t("next")}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              ) : (
                <>
                  <Button onClick={handleComplete}>
                    {t("finish")}
                    <CheckCircle className="ml-2 h-4 w-4" />
                  </Button>
                </>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Named for what is actually lost, not "are you sure": the point is that
          the user learns these questions exist and where they live. */}
      <AlertDialog open={showSkipWarning} onOpenChange={setShowSkipWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("skip_warning_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {!areasAnswered && !hoursAnswered
                ? t("skip_warning_both")
                : !areasAnswered
                  ? t("skip_warning_areas")
                  : t("skip_warning_hours")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {/* Setting it up is the emphasised action; skipping stays available
              but does not get the primary button. */}
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => void handleComplete()}>
              {t("skip_warning_confirm")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleSkipCancel}>
              {t("skip_warning_setup")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
